import React, { useCallback, useEffect, useReducer } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { pageContext } from 'ctm-util-functions';
import { TechnicalError } from 'ctm-vertical-base/base/components/technical_error/technical_error';
import { getBrand } from 'ctm-app/client/store/selectors';
import { reducer } from './reducer';
import { convertObjectKeysToLowercase } from '../utils';
import { useStyles } from './prefill_verify_address_styles';
import { PrefillQuoteCTAButtons } from '../anon_prefill/prefill_quote_cta_buttons';
import { MechaAlertBanner } from '../mecha_components/mecha_alert_banner/mecha_alert_banner';
import { PrefillAddressVerification } from './prefill_address_verification';
import {
    setRemainingAttempts,
    setFailAttemptBanner,
    setLockoutTime,
    setLockedOut,
    setIncompleteEntry,
    setFutureTimestamp,
    setCallingVerify,
    setUnhandledErrorState,
    setShowSplash } from './actions';
import { updateState, updateTokens } from './home_and_contents_utils';
import { PrefillWelcomeBackSplash } from '../anon_prefill/prefill_welcome_back_splash';

const Spinner = styled.div`
    margin: 0 auto;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 8.5px solid #0B60B7;
    border-bottom-color: transparent;
    animation: spin 1s linear infinite;
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const _PrefillVerifyAddress = (props) => {
    const classes = useStyles();
    const {
        anonId,
        journeyUrl,
        env,
        brandCode,
        address,
        gnafId,
        addressLabel,
        streetName,
        streetNumber,
        postcode,
        suburb,
        addressState,
        clearAddress,
        trackVerifyFail,
        trackLockout,
        trackVerify,
        trackQuoteStart,
        mapToState,
        userHasSavedResults
    } = props;

    const accessToken = localStorage.getItem('access_token');

    const initialState = {
        remainingAttempts: 0,
        failAttemptBanner: false,
        lockedOut: false,
        incompleteEntry: false,
        lockoutTime: localStorage.getItem('lockoutTimePVHnc') || null,
        futureTimestamp: localStorage.getItem('futureTimestampPVHnc') || null,
        callingVerify: false,
        unhandledErrorState: false,
        showSplash: true,
        userHasSavedResults: false
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const hasManualAddress = !!streetName && !!streetNumber && !!postcode && !!suburb && !!addressState;
    const hasGnafAddress = !!gnafId && !!addressLabel;
    const continueDisabled = !(hasGnafAddress || hasManualAddress) || state.callingVerify || state.lockedOut;

    // Handle timeout functionality - modified from postcode_confirmation PR 878
    // Check if the current time is equal to the future time
    // We useCallback to memoize the function and stop recreating it on every render
    let intervalId;
    const checkTimestamp = useCallback(() => {
        const currentTimestamp = moment().format('HH:mm:ss');

        if (currentTimestamp >= state.futureTimestamp) {
            dispatch(setLockoutTime(0));
            dispatch(setLockedOut(false));
            clearInterval(intervalId);
            dispatch(setFutureTimestamp(null));
            localStorage.removeItem('futureTimestampPVHnc');
            localStorage.removeItem('lockoutTimePVHnc');
            dispatch(setIncompleteEntry(false));
        }
    }, [state.futureTimestamp]);

    useEffect(() => {
        if (state.futureTimestamp !== null) {
            intervalId = setInterval(checkTimestamp, 1000);
        }
    }, [state.futureTimestamp]);

    const startNewQuote = () => {
        clearAddress();
        pageContext.getProps().browserHistory.push({ pathname: '/home-contents-insurance/journey/start', search: window?.location?.search });
    };

    const resumeQuoteFunc = async () => {
        if (continueDisabled) {
            dispatch(setIncompleteEntry(true));
            return;
        }
        dispatch(setCallingVerify(true));

        // Call verify endpoint
        const verifyEndpoint = `${journeyUrl}${env === 'local' ? '' : '/'}journey/prefill/verify`;
        const payload = {
            postcode: address.getIn(['postcode']),
            address
        };

        await axios
            .post(
                verifyEndpoint,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    },
                    params: {
                        anonymous_id: anonId,
                        brand_code: brandCode
                    }
                }
            )
            .then((response) => {
                // Mapping and handling to be handled in 46175
                if (response.data.prefill.lastViewedPage === '/home-contents-insurance/journey/results' && response?.data?.quote?.payload?.results) {
                    userHasSavedResults();
                }
                setCallingVerify(false);
                const prefill = response?.data?.prefill;
                mapToState(response.data, accessToken, prefill?.refreshToken);
                trackQuoteStart();

                // Map browser history
                const schemaAsOrderedList = pageContext.getProps().models.pageSchemaModel.schemaAsOrderedList;
                const pageName = response.data.prefill.lastViewedPage.split('/').pop();

                // TODO 0 index, does this need to be increased?
                let history = schemaAsOrderedList.slice(0, schemaAsOrderedList.indexOf(pageName));
                if (history.length === 0) {
                    history = ['prefill_check'];
                }

                // This will replace the prefill_verify page with start
                window.history.replaceState(null, 'Start', '/home-contents-insurance/journey/prefill_check');
                for (let i = 0; i < history.length; i++) {
                    // Exclude pages that don't belong
                    const route = `/home-contents-insurance/journey/${history[i]}`;
                    window.history.pushState(null, history[i], route);
                }
                window.history.go(history.length);

                pageContext.getProps().browserHistory.push(prefill?.lastViewedPage);
                return response;
            })
            .catch((err) => {
                dispatch(setIncompleteEntry(false));
                const lowercaseHeader = convertObjectKeysToLowercase(err.response.headers);
                dispatch(setCallingVerify(false));
                switch (err.response.status) {
                    // Journey not found
                    case 404:
                        trackVerifyFail();
                        // TODO remove this, it's cheating until tyk is ready
                        dispatch(setRemainingAttempts(parseInt(lowercaseHeader['x-ctm-gateway-remaining-attempts'], 10)));
                        dispatch(setLockedOut(false));
                        dispatch(setFailAttemptBanner(true));
                        break;
                    case 429:
                        // Tyk lockout case
                        trackLockout();

                        // Timeoout logic
                        const convertSecondsToMinutesLockoutTime = Math.ceil(parseInt(lowercaseHeader['x-ctm-gateway-remaining-lockout-time'], 10) / 60);
                        dispatch(setLockoutTime(convertSecondsToMinutesLockoutTime));
                        const _currentTimestamp = moment().format('HH:mm:ss');
                        const _futureTimestamp = moment(_currentTimestamp, 'HH:mm:ss')
                            .add(parseInt(lowercaseHeader['x-ctm-gateway-remaining-lockout-time']), 'seconds')
                            .format('HH:mm:ss');
                        dispatch(setFutureTimestamp(_futureTimestamp));
                        localStorage.setItem('lockoutTimePVHnc', convertSecondsToMinutesLockoutTime);
                        localStorage.setItem('futureTimestampPVHnc', _futureTimestamp);

                        dispatch(setLockedOut(true));
                        dispatch(setFailAttemptBanner(false));
                        break;
                    default:
                        // Unhandled failure of Everest, show generic error page
                        dispatch(setUnhandledErrorState(true));
                        break;
                }
            });
    };

    if (state.unhandledErrorState) {
        return (
            <div className={classes.errorWrapper}>
                <TechnicalError />
            </div>
        );
    }

    if (state.showSplash) {
        // reset state
        return (
            <PrefillWelcomeBackSplash
                startNewQuote={startNewQuote}
                proceedToVerify={() => {
                    clearAddress();
                    dispatch(setShowSplash(false));
                }}
            />
        );
    }

    if (!state.showSplash && !window.dataLayer.some((item) => item.actionStep === 'home_contents prefill verify')) {
        trackVerify();
    }

    return (
        <div className={classes.hnc_prefill_verify_wrapper}>
            {state.callingVerify
                ? (
                    <div className={classes.fullscreenLoaderWrapper}>
                        <Spinner />
                    </div>
                )
                : null}
            <div className={classes.header_wrapper}>
                <h2 className={classes.header_two}>
                    Welcome back!
                </h2>
                <h4>Confirm your address to resume</h4>
            </div>
            {state.lockedOut ? (
                <div className={classes.banner_wrapper}>
                    <MechaAlertBanner
                        header={'You\'ve used all your attempts.'}
                        content={'Please try again in'}
                        contentBold={` ${state.lockoutTime} minutes `}
                        startNewQuote={startNewQuote}
                        justify="left"
                        darkRed
                        iconShape="cross"
                    />
                </div>
            )
                : null}
            {state.failAttemptBanner ? (
                <div className={classes.banner_wrapper}>
                    <MechaAlertBanner
                        header="We are unable to match a quote for these details"
                        content={'Please try again '}
                        startNewQuote={startNewQuote}
                        justify="left"
                        darkRed
                        iconShape="triangle"
                        data-testid="failAttemptBanner"
                    />
                </div>
            )
                : null}
            <PrefillAddressVerification
                lockedOut={state.lockedOut}
                invalidAdress={state.failAttemptBanner}
                incompleteEntry={state.incompleteEntry}
            />
            <PrefillQuoteCTAButtons
                continueDisabled={continueDisabled}
                startNewQuote={startNewQuote}
                resumeQuoteFunc={resumeQuoteFunc}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    const anonId = state.getIn(['vertical', 'quote', 'form', 'helpers', 'prefill', 'userAnonymousId']) || sessionStorage.getItem('user_anonymous_id');
    const journeyUrl = state.getIn(['serverConfig', 'journeyServiceBaseUrl']);
    const env = state.getIn(['serverConfig', 'env']);
    const brandCode = getBrand(state);

    // Lookup address
    const address = state.getIn(['vertical', 'quote', 'form', 'property', 'address']);
    const gnafId = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'gnafId']);
    const addressLabel = state.getIn(['vertical', 'quote', 'form', 'helpers', 'propertyAddressLabel']);

    // Manual address
    const streetName = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'streetName']);
    const streetNumber = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'streetNumber']);
    const postcode = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'postcode']);
    const suburb = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'suburb']);
    const addressState = state.getIn(['vertical', 'quote', 'form', 'property', 'address', 'state']);
    return { anonId, brandCode, journeyUrl, env, address, gnafId, addressLabel, streetName, streetNumber, postcode, suburb, addressState };
};

const mapDispatchToProps = (dispatch) => ({
    trackVerifyFail: () => {
        dispatch({
            type: 'PREFILL_VERIFY_FAIL'
        });
    },
    trackLockout: () => {
        dispatch({
            type: 'PREFILL_LOCKOUT'
        });
    },
    trackQuoteStart: () => {
        dispatch({
            type: 'PREFILL_QUOTE_START'
        });
    },
    trackVerify: () => {
        dispatch({
            type: 'PREFILL_VERIFY'
        });
    },
    clearAddress: () => {
        dispatch({
            type: 'RESET_FORM_STATE_VALUES',
            identifier: 'property.address',
            stateKey: 'vertical.quote'
        });
        dispatch({
            type: 'RESET_VALUES',
            identifiers: [
                'helpers.propertyAddressLabel',
                'helpers.unableToFindAddress_property'
            ],
            stateKey: 'vertical.quote'
        });
        dispatch({
            type: 'RESET_TOUCHES',
            stateKey: 'vertical.quote',
            identifiers: [
                'property.address.postcode',
                'helpers.propertyAddressLabel',
                'helpers.propertyPostcode'
            ]
        });
    },
    mapToState: (responsePayload, accessToken, refreshToken) => {
        // TODO: Use update tokens method when data is valid
        updateTokens(dispatch, accessToken, refreshToken);
        updateState(dispatch, responsePayload);
    },
    userHasSavedResults: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: true
        });
    }
});

_PrefillVerifyAddress.propTypes = {
};

const PrefillVerifyAddress = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PrefillVerifyAddress);

export { _PrefillVerifyAddress, PrefillVerifyAddress };
